import { tokens } from "lib/colors"
import { LanguageContext } from "context/LanguageContext"
import Icon from "lib/components/Icons"
import Text from "lib/components/Text"
import dynamic from "next/dynamic"
import { createRef, useContext, useState } from "react"
import { optimizeImageCloudinary } from "lib/helpers"
import { isDesktop } from "react-device-detect"
import Slider from "react-slick"
import { benefitItems, Benefits } from "../types/benefit"

import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"

const ImageComponent = dynamic(() => import("lib/components/Image"), { ssr: false })

const data = [
  {
    id: 1,
    title: "Dr. Tirta",
    icon: "icon-speaker-service",
    subtitle: "Leadership & Soft-Skills",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675975/corporate_solution/experience-speaker-3.webp",
  },
  {
    id: 2,
    title: "Ade Rai",
    subtitle: "Leadership & Soft-Skills",
    icon: "icon-skill-booster",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675973/corporate_solution/experience-speaker-1.webp",
  },
  {
    id: 3,
    title: "Veddriq (Climber olympic)",
    icon: "icon-speaker-service",
    subtitle: "Leadership & Soft-Skills",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675972/corporate_solution/experience-speaker-2.webp",
  },
  {
    id: 5,
    title: "Becky Tumewu",
    icon: "icon-speaker-service",
    subtitle: "Communication",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675978/corporate_solution/experience-speaker-5.webp",
  },
  {
    id: 6,
    title: "Cindy Owada",
    icon: "icon-speaker-service",
    subtitle: "Food & Beverage",
    image:
      "https://res.cloudinary.com/https-www-pasartrainer-com/image/upload/v1753675968/corporate_solution/experience-speaker-4.webp",
  },
]

function LearningFestivalSpeaker() {
  const { useTranslation: t } = useContext(LanguageContext)
  const benefits = t("benefits_learning_festival_speakers") as unknown as Benefits
  const [currentSlideIndex, setCurrentSlide] = useState<number>(0)
  const customeSlider: {
    current: any
  } = createRef()

  const gotoNext = () => {
    customeSlider.current.slickNext()
  }

  const gotoPrev = () => {
    customeSlider.current.slickPrev()
  }

  const settings = {
    beforeChange: (_prev: number, next: number) => {
      setCurrentSlide(next)
    },
    dots: false,
    dotsClass: "slick-dots slick-thumb",
    arrows: false,
    infinite: true,
    slidesToShow: isDesktop ? 3 : 1,
    slidesToScroll: isDesktop ? 3 : 1,
    autoplay: false,
    cssEase: "linear",
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  }

  return (
    <div className="w-full bg-white px-5 pt-5 pb-3 rounded-2xl drop-shadow-[0px_4px_12px_rgba(23,15,73,0.08)]">
      <div className="flex flex-col gap-2 lg:gap-3">
        <div className="flex flex-col gap-2">
          <div className="slider-container">
            <div className="-mx-1.5">
              <Slider ref={customeSlider} {...settings}>
                {data.map((item) => (
                  <div key={item.id} className="px-1.5">
                    <div className="p-3 bg-neutral-20 rounded-xl flex flex-col">
                      <div className="relative aspect-[16/9] w-full">
                        <ImageComponent
                          src={optimizeImageCloudinary(item.image, "high", "w_650")}
                          alt={`${item.id}`}
                          objectFit="cover"
                          className="rounded-xl object-cover"
                        />
                      </div>
                      <div className="flex flex-row items-center justify-between mt-3">
                        <div className="flex flex-col">
                          <Text weight="semibold" customClass="text-sm lg:text-xl">
                            {item.title}
                          </Text>
                          <Text customClass="text-xs lg:text-base">{item.subtitle}</Text>
                        </div>
                        <Icon pathName={item.icon} styles="w-[17px] h-4 lg:w-[21px] lg:h-[24px]" />
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between">
            <div className="px-4 flex gap-2 place-content-center">
              {Array.from({
                length: isDesktop ? Math.ceil(data.length / 3) : data.length,
              }).map((_, indexDot) => {
                let bgGray = "bg-neutral-40"
                const activeIndex = isDesktop
                  ? Math.floor(currentSlideIndex / 3)
                  : currentSlideIndex

                if (indexDot === activeIndex) {
                  bgGray = "bg-primary"
                }

                return (
                  <div
                    tabIndex={0}
                    aria-label="Slider"
                    role="button"
                    onClick={() =>
                      customeSlider.current.slickGoTo(isDesktop ? indexDot * 3 : indexDot)
                    }
                    key={indexDot}
                    className={`${
                      indexDot === activeIndex ? "w-4" : "w-2"
                    } h-2 ${bgGray} rounded-full cursor-pointer`}
                  />
                )
              })}
            </div>
            <div className="flex flex-row">
              <Icon
                iconName="arrowLeft"
                styles="bg-white hover:bg-primary/15 h-8 w-8 p-1 rounded-full hover:cursor-pointer hover:text-primary"
                onClick={() => gotoPrev()}
                color={tokens.neutral[50]}
              />
              <Icon
                iconName="arrow-right"
                color={tokens.neutral[50]}
                styles="bg-white hover:bg-primary/15 h-8 w-8 p-1 rounded-full hover:cursor-pointer hover:text-primary"
                onClick={() => gotoNext()}
              />
            </div>
          </div>
        </div>

        <hr className="border-t-1 border-dashed border-neutral-40 my-1" />
        <div className="flex flex-col gap-1.5">
          <Text weight="semibold" customClass="text-sm lg:text-lg">
            {benefits.title}
          </Text>
          {benefitItems.map((key) => (
            <div key={key} className="flex flex-row items-start lg:items-center gap-1">
              <div className="w-4 lg:w-[28px]">
                <Icon
                  iconName="check-badge-solid"
                  color={tokens.info.DEFAULT}
                  styles="w-4 h-5 lg:w-[28px] lg:h-[28px]"
                />
              </div>
              <Text customClass="text-sm lg:text-base" weight="medium">
                {benefits[key].title}:{" "}
                <span className="font-normal">{benefits[key].description}</span>
              </Text>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default LearningFestivalSpeaker
